import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import EditableText from "@/components/editor/EditableText";
import { cdn, srcSet } from "@/lib/img";

const MISTY_IMG = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/fjjgmrzd_IMG_8812.jpeg";
const MOSSY_ROCKS_IMG = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/shg7ofp3_IMG_8836.jpeg";
const ROCK_CREVICE_IMG = "https://customer-assets.emergentagent.com/job_whale-cove-retreat/artifacts/6snqon67_IMG_8834.jpeg";

export default function Trails() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="trails" data-testid="trails-section" className="wc-section bg-white" ref={ref}>
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <p className="font-accent italic text-wc-teal text-base mb-3">Trails & Waterfront</p>
          <EditableText contentKey="trails_heading" as="h2"
            className="font-display text-wc-forest text-3xl sm:text-4xl lg:text-5xl font-semibold mb-8 max-w-3xl"
            defaultValue="From campsite to coastline." />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Trail description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-1 space-y-6"
          >
            <EditableText contentKey="trails_p1" as="p"
              className="font-body text-wc-slate text-base leading-relaxed"
              defaultValue="The trail starts right from your campsite. A soft path through spruce and birch — moss underfoot, ferns brushing your ankles. The air cools as you descend. You hear the water first." />
            <EditableText contentKey="trails_p2" as="p"
              className="font-body text-wc-slate text-base leading-relaxed"
              defaultValue="Then the trees open up and the rocky shore stretches out in front of you. At high tide, the waves crash against the rocks at your feet. At low tide, the ocean has retreated hundreds of meters, revealing tide pools and ancient stone." />
            <EditableText contentKey="trails_p3" as="p"
              className="font-body text-wc-slate text-base leading-relaxed"
              defaultValue="It's a different world at every tide. Come back in six hours and you won't believe it's the same place." />

            {/* Trail map placeholder */}
            <div className="wc-image-placeholder h-48 rounded-md">
              Trail Map — Hand-drawn style<br />
              (Coming soon)
            </div>
          </motion.div>

          {/* Images */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <div className="rounded-sm overflow-hidden aspect-[3/4]">
              <img
                src={cdn(MOSSY_ROCKS_IMG, 700)}
                srcSet={srcSet(MOSSY_ROCKS_IMG)}
                sizes="(max-width: 640px) 100vw, 33vw"
                alt="Moss-covered rocks and forest floor along the trail"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover wc-img"
              />
            </div>
            <div className="rounded-sm overflow-hidden aspect-[3/4]">
              <img
                src={cdn(ROCK_CREVICE_IMG, 700)}
                srcSet={srcSet(ROCK_CREVICE_IMG)}
                sizes="(max-width: 640px) 100vw, 33vw"
                alt="Rock crevice with birch tree along the trail to the shore"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover wc-img"
              />
            </div>
            <div className="rounded-sm overflow-hidden aspect-[4/3]">
              <img
                src={cdn(MISTY_IMG, 900)}
                srcSet={srcSet(MISTY_IMG)}
                sizes="(max-width: 640px) 100vw, 33vw"
                alt="Fog rolling over the rocky shore at Whale Cove"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover wc-img"
              />
            </div>
            <div className="wc-image-placeholder rounded-sm aspect-[4/3]">
              Rocky shore at low tide — tide pools visible
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
